import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  KanbanSquare,
  Plus,
  Search,
  SlidersHorizontal,
  Loader2,
  RefreshCw,
  Inbox,
  X,
  Archive,
  ArchiveRestore,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  ApplicationDetail,
  ApplicationStatus,
  ApplicationSummary,
  EmploymentType,
  KanbanColumn,
  Organization,
  Resume,
} from '../types';
import { applicationsService, organizationsService, resumeService, getApiErrorMessage } from '../services/api';
import Board from '../components/kanban/Board';
import KanbanSkeleton from '../components/kanban/KanbanSkeleton';
import MetricsBar from '../components/kanban/MetricsBar';
import CalendarStrip from '../components/kanban/CalendarStrip';
import ApplicationDrawer from '../components/kanban/ApplicationDrawer';
import ApplicationFormModal from '../components/kanban/ApplicationFormModal';
import ArchivedCard from '../components/kanban/ArchivedCard';
import { COLUMN_META, EMPLOYMENT_LABELS, resumeLabel } from '../components/kanban/kanbanConstants';
import { collectUpcomingInterviews, computeMetrics } from '../utils/applicationMetrics';

interface Filters {
  search: string;
  resumeId: number | 'all';
  orgId: number | 'all';
  employmentType: EmploymentType | 'ALL';
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: Filters = {
  search: '',
  resumeId: 'all',
  orgId: 'all',
  employmentType: 'ALL',
  dateFrom: '',
  dateTo: '',
};

const Applications: React.FC = () => {
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [archivedApps, setArchivedApps] = useState<ApplicationSummary[]>([]);
  const [view, setView] = useState<'board' | 'archived'>('board');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [resumes, setResumes] = useState<Resume[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState<ApplicationStatus>('APPLIED');
  const [confirmArchiveAll, setConfirmArchiveAll] = useState(false);
  const [archiveAllBusy, setArchiveAllBusy] = useState(false);

  const fetchBoard = useCallback(async () => {
    try {
      const res = await applicationsService.getBoard();
      setColumns(res.data.columns);
      setError('');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not load your board.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchArchived = useCallback(async () => {
    try {
      const res = await applicationsService.getArchived();
      setArchivedApps(res.data);
    } catch {
      // Non-fatal: the archived tab will show an empty state.
    }
  }, []);

  const handleBoardChanged = useCallback(() => {
    fetchBoard();
    fetchArchived();
  }, [fetchBoard, fetchArchived]);

  const fetchMeta = useCallback(async () => {
    try {
      const [resumesRes, orgsRes] = await Promise.all([
        resumeService.getAllResumes(),
        organizationsService.list(),
      ]);
      setResumes(resumesRes.data);
      setOrganizations(orgsRes.data);
    } catch {
      // Non-fatal: forms and filters will surface empty pickers.
    }
  }, []);

  useEffect(() => {
    fetchBoard();
    fetchArchived();
    fetchMeta();
  }, [fetchBoard, fetchArchived, fetchMeta]);

  const isFiltered = useMemo(
    () =>
      filters.search.trim() !== '' ||
      filters.resumeId !== 'all' ||
      filters.orgId !== 'all' ||
      filters.employmentType !== 'ALL' ||
      filters.dateFrom !== '' ||
      filters.dateTo !== '',
    [filters]
  );

  const filteredColumns = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return columns.map((column) => ({
      ...column,
      applications: column.applications.filter((card) => {
        if (filters.resumeId !== 'all' && card.resumeId !== filters.resumeId) return false;
        if (filters.orgId !== 'all' && card.organizationId !== filters.orgId) return false;
        if (filters.employmentType !== 'ALL' && card.employmentType !== filters.employmentType) return false;
        if (filters.dateFrom && card.appliedAt && card.appliedAt < filters.dateFrom) return false;
        if (filters.dateTo && card.appliedAt && card.appliedAt > filters.dateTo) return false;
        if (q) {
          const haystack = [
            card.jobTitle,
            card.organizationName,
            card.location,
            card.recruiterName,
            card.resumeLabel,
          ]
            .filter((v): v is string => Boolean(v))
            .join(' ')
            .toLowerCase();
          if (!haystack.includes(q)) return false;
        }
        return true;
      }),
    }));
  }, [columns, filters]);

  const filteredMetrics = useMemo(() => computeMetrics(filteredColumns), [filteredColumns]);
  const upcomingEntries = useMemo(() => collectUpcomingInterviews(filteredColumns), [filteredColumns]);
  const totalCards = useMemo(() => columns.reduce((sum, c) => sum + c.applications.length, 0), [columns]);
  const filteredTotal = useMemo(() => filteredColumns.reduce((sum, c) => sum + c.applications.length, 0), [filteredColumns]);

  const handleStatusChange = async (applicationId: number, status: ApplicationStatus) => {
    try {
      await applicationsService.updateStatus(applicationId, status);
      setColumns((prev) =>
        prev.map((column) => ({
          ...column,
          applications: column.applications.map((card) =>
            card.id === applicationId ? { ...card, status } : card
          ),
        }))
      );
      toast.success(`Moved to ${COLUMN_META[status].label}.`, { id: 'status-toast' });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not move the application.'));
      fetchBoard();
    }
  };

  const handleSaved = (saved: ApplicationDetail) => {
    setSelectedApplicationId((current) => current ?? saved.id);
    fetchBoard();
  };

  const handleArchive = async (application: ApplicationSummary) => {
    try {
      await applicationsService.setArchived(application.id, true);
      toast.success(`${application.jobTitle} archived. Restore it anytime from the Archived tab.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not archive the application.'));
      return;
    }
    handleBoardChanged();
  };

  const handleRestore = async (application: ApplicationSummary) => {
    try {
      await applicationsService.setArchived(application.id, false);
      toast.success(`${application.jobTitle} restored to the board.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not restore the application.'));
      return;
    }
    handleBoardChanged();
  };

  const handleArchiveAll = async () => {
    setArchiveAllBusy(true);
    try {
      const res = await applicationsService.archiveAll(true);
      toast.success(
        res.data.updated > 0
          ? `Archived ${res.data.updated} application${res.data.updated === 1 ? '' : 's'} to the Archived tab.`
          : 'Nothing to archive — your board is already empty.'
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not archive the board.'));
    } finally {
      setArchiveAllBusy(false);
      setConfirmArchiveAll(false);
    }
    handleBoardChanged();
  };

  const handleRestoreAll = async () => {
    try {
      const res = await applicationsService.archiveAll(false);
      toast.success(
        res.data.updated > 0
          ? `Restored ${res.data.updated} application${res.data.updated === 1 ? '' : 's'} to the board.`
          : 'No archived applications to restore.'
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not restore the archived applications.'));
      return;
    }
    handleBoardChanged();
  };

  const resetFilters = () => setFilters(EMPTY_FILTERS);

  const selectClass =
    'h-9 px-2.5 border border-zinc-200 rounded-btn text-xs text-zinc-600 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500';

  return (
    <div className="max-w-[1600px] mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground inline-flex items-center gap-2.5">
            <KanbanSquare className="w-7 h-7 text-brand-500 hidden sm:block" />
            Job Applications
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Track every application from applied to hired. Drag cards between columns to update status.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center bg-white border border-zinc-200 rounded-btn p-0.5">
            <button
              onClick={() => setView('board')}
              className={`h-8 px-3 text-xs font-medium rounded-btn transition-colors ${
                view === 'board' ? 'bg-brand-500 text-white' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              Board
            </button>
            <button
              onClick={() => setView('archived')}
              className={`h-8 px-3 text-xs font-medium rounded-btn inline-flex items-center gap-1.5 transition-colors ${
                view === 'archived' ? 'bg-brand-500 text-white' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <Archive className="w-3.5 h-3.5" />
              Archived
              {archivedApps.length > 0 && (
                <span className={`rounded-full px-1.5 text-[10px] font-semibold ${
                  view === 'archived' ? 'bg-white/20' : 'bg-zinc-100 text-zinc-500'
                }`}>
                  {archivedApps.length}
                </span>
              )}
            </button>
          </div>
          <button
            onClick={() => {
              setFormStatus('APPLIED');
              setShowForm(true);
            }}
            className="h-10 px-4 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2 shadow-soft"
          >
            <Plus className="w-4 h-4" /> New application
          </button>
        </div>
      </div>

      {isLoading ? (
        <KanbanSkeleton />
      ) : error ? (
        <div className="bg-white border border-zinc-200 rounded-card p-12 text-center">
          <Loader2 className="w-8 h-8 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-500 mb-4">{error}</p>
          <button
            onClick={() => {
              setIsLoading(true);
              fetchBoard();
            }}
            className="h-10 px-4 inline-flex items-center gap-2 border border-zinc-200 hover:bg-zinc-50 text-zinc-600 text-sm font-medium rounded-btn transition-colors"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      ) : view === 'archived' ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-zinc-500">
              Completed applications live here — off the board and out of your metrics, but still available with their resume and interviews. Restore one to keep tracking it.
            </p>
            {archivedApps.length > 0 && (
              <button
                onClick={handleRestoreAll}
                title="Move every archived application back to the board"
                className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-medium rounded-btn border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-colors"
              >
                <ArchiveRestore className="w-3.5 h-3.5" />
                Restore all
              </button>
            )}
          </div>
          {archivedApps.length === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-card p-14 text-center">
              <div className="w-14 h-14 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ArchiveRestore className="w-6 h-6 text-zinc-400" />
              </div>
              <h3 className="font-display font-semibold text-lg text-foreground mb-2">Nothing archived yet</h3>
              <p className="text-sm text-zinc-500 max-w-sm mx-auto">
                Archive an application (e.g. once you are hired) and it will wait here, ready to be restored whenever you need.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {archivedApps.map((app) => (
                <ArchivedCard
                  key={app.id}
                  application={app}
                  onClick={() => setSelectedApplicationId(app.id)}
                  onRestore={() => handleRestore(app)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <MetricsBar metrics={filteredMetrics} filtered={isFiltered} />

          <CalendarStrip interviews={upcomingEntries} />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />
              <input
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                placeholder="Search title, company, recruiter…"
                className="h-9 pl-8 pr-3 w-64 max-w-full border border-zinc-200 rounded-btn text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            {totalCards > 0 && (
              <button
                onClick={() => setConfirmArchiveAll(true)}
                title="Move every application on the board to the Archived tab"
                className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-medium rounded-btn border border-zinc-200 text-zinc-600 hover:bg-zinc-50 transition-colors"
              >
                <Archive className="w-3.5 h-3.5" />
                Archive all
              </button>
            )}
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs font-medium rounded-btn border transition-colors ${
                showFilters || isFiltered
                  ? 'border-brand-300 text-brand-700 bg-brand-50'
                  : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Filters
              {isFiltered && <span className="w-4 h-4 rounded-full bg-brand-500 text-white text-[10px] flex items-center justify-center">{filteredColumns.reduce((s, c) => s + c.applications.length, 0)}</span>}
            </button>
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap items-center gap-3 bg-white border border-zinc-200 rounded-card px-4 py-3">
                <select
                  value={filters.resumeId}
                  onChange={(e) => setFilters((prev) => ({ ...prev, resumeId: Number(e.target.value) || 'all' }))}
                  className={selectClass}
                  title="Resume"
                >
                  <option value="all">All resumes</option>
                  {resumes.filter((r) => !r.disabled).map((resume) => (
                    <option key={resume.id} value={resume.id}>{resumeLabel(resume)}</option>
                  ))}
                </select>
                <select
                  value={filters.orgId}
                  onChange={(e) => setFilters((prev) => ({ ...prev, orgId: Number(e.target.value) || 'all' }))}
                  className={selectClass}
                  title="Company"
                >
                  <option value="all">All companies</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </select>
                <select
                  value={filters.employmentType}
                  onChange={(e) => setFilters((prev) => ({ ...prev, employmentType: (e.target.value as EmploymentType | 'ALL') }))}
                  className={selectClass}
                  title="Employment type"
                >
                  <option value="ALL">All employment types</option>
                  {(Object.keys(EMPLOYMENT_LABELS) as EmploymentType[]).map((type) => (
                    <option key={type} value={type}>{EMPLOYMENT_LABELS[type]}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                  className={selectClass}
                  title="Applied from"
                />
                <span className="text-xs text-zinc-400">to</span>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                  className={selectClass}
                  title="Applied to"
                />
                {(isFiltered || showFilters) && (
                  <button
                    onClick={resetFilters}
                    className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-700 border border-zinc-200 hover:bg-zinc-50 rounded-btn transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Clear
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {totalCards === 0 ? (
            <div className="bg-white border border-zinc-200 rounded-card p-14 text-center">
              <div className="w-14 h-14 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Inbox className="w-6 h-6 text-brand-500" />
              </div>
              <h3 className="font-display font-semibold text-lg text-foreground mb-2">Your board is empty</h3>
              <p className="text-sm text-zinc-500 max-w-sm mx-auto mb-6">
                Add your first job application and start tracking interviews, offers, and follow-ups.
              </p>
              <button
                onClick={() => {
                  setFormStatus('APPLIED');
                  setShowForm(true);
                }}
                className="h-10 px-5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Add your first application
              </button>
            </div>
          ) : (
            <>
              <div>
                <Board
                  columns={filteredColumns}
                  setColumns={setColumns}
                  onStatusChange={handleStatusChange}
                  onCardClick={(application) => setSelectedApplicationId(application.id)}
                  onArchive={handleArchive}
                  onQuickAdd={(status) => {
                    setFormStatus(status);
                    setShowForm(true);
                  }}
                />
              </div>
              <p className="text-xs text-zinc-400">
                Showing {filteredTotal} of {totalCards} application{totalCards === 1 ? '' : 's'}
                {isFiltered ? ' (filtered)' : ''}. Drag a card to move it between status columns.
              </p>
            </>
          )}
        </div>
      )}

      <AnimatePresence>
        {selectedApplicationId !== null && (
          <ApplicationDrawer
            key={selectedApplicationId}
            applicationId={selectedApplicationId}
            resumes={resumes}
            onClose={() => setSelectedApplicationId(null)}
            onBoardChanged={handleBoardChanged}
          />
        )}
      </AnimatePresence>

      {showForm && (
        <ApplicationFormModal
          resumes={resumes}
          initialStatus={formStatus}
          onClose={() => setShowForm(false)}
          onSaved={handleSaved}
        />
      )}

      <AnimatePresence>
        {confirmArchiveAll && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-900/40 p-4"
            onClick={() => !archiveAllBusy && setConfirmArchiveAll(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              className="w-full max-w-sm bg-white rounded-card shadow-elevated p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="w-10 h-10 bg-brand-50 rounded-full flex items-center justify-center">
                  <Archive className="w-5 h-5 text-brand-600" />
                </div>
                <button
                  onClick={() => setConfirmArchiveAll(false)}
                  className="text-zinc-400 hover:text-zinc-600 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <h3 className="font-display font-semibold text-lg text-foreground mb-1">Archive the whole board?</h3>
              <p className="text-sm text-zinc-500 mb-5">
                {totalCards > 0
                  ? `All ${totalCards} application${totalCards === 1 ? '' : 's'} currently on the board will move to the Archived tab. Nothing is deleted — you can restore them anytime.`
                  : 'There are no applications on the board to archive.'}
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setConfirmArchiveAll(false)}
                  disabled={archiveAllBusy}
                  className="h-9 px-4 border border-zinc-200 text-zinc-600 text-sm font-medium rounded-btn hover:bg-zinc-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleArchiveAll}
                  disabled={archiveAllBusy || totalCards === 0}
                  className="h-9 px-4 bg-brand-500 hover:bg-brand-600 disabled:opacity-50 text-white text-sm font-medium rounded-btn transition-colors inline-flex items-center gap-2"
                >
                  {archiveAllBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                  Archive all
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Applications;